import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FaChevronLeft, FaChevronRight, FaArrowRight, FaShieldAlt, FaBolt } from 'react-icons/fa'

import { get_banners } from '../store/reducers/bannerreducer'
import { Skeleton } from './ui'

// ─────────────────────────────────────────────────────────────
// Banner — modern responsive marketplace hero.
//
// Layout:
//   > md-lg : shared-height 2/1 split — main carousel + 2 stacked side tiles.
//   < md-lg : main on top, 2 side tiles in a 2-col grid below.
//
// Desktop uses one bounded height; tablet/mobile use compact aspect ratios.
// Data fetch (`get_banners`) is unchanged.
// ─────────────────────────────────────────────────────────────

const useSlider = (count = 0, interval = 5000) => {
    const [idx, setIdx] = useState(0)
    const next = useCallback(() => setIdx(i => (i + 1) % Math.max(count, 1)), [count])
    const prev = useCallback(() => setIdx(i => (i - 1 + Math.max(count, 1)) % Math.max(count, 1)), [count])
    useEffect(() => {
        if (count <= 1) return
        const t = setInterval(next, interval)
        return () => clearInterval(t)
    }, [next, count, interval])
    useEffect(() => { setIdx(0) }, [count])
    return { idx, next, prev, setIdx }
}

const linkTarget = (link) => {
    if (!link) return null
    return link.startsWith('http') ? link : `/product/details/${link}`
}

// ── Main hero ─────────────────────────────────────────────────
const MainHero = ({ banners = [] }) => {
    const { idx, next, prev, setIdx } = useSlider(banners.length, 5000)

    // Fallback premium card when no banner data
    if (!banners.length) {
        return (
            <div className='relative h-full w-full overflow-hidden rounded-card border border-brand-800/40 bg-gradient-to-br from-brand-700 via-brand-800 to-brand-900 text-white shadow-card-hover'>
                {/* Soft decorative lighting — premium feel without childish emoji */}
                <div className='absolute inset-0 opacity-60 pointer-events-none' aria-hidden='true' style={{
                    backgroundImage:
                        'radial-gradient(60% 50% at 15% 10%, rgba(255,255,255,0.18) 0, transparent 60%),' +
                        'radial-gradient(45% 35% at 85% 85%, rgba(249,115,22,0.22) 0, transparent 60%),' +
                        'radial-gradient(50% 45% at 70% 20%, rgba(139,92,246,0.22) 0, transparent 60%)'
                }} />
                {/* Subtle grid texture */}
                <div className='absolute inset-0 opacity-[0.05] pointer-events-none' aria-hidden='true' style={{
                    backgroundImage:
                        'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px),' +
                        'linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
                    backgroundSize: '32px 32px',
                }} />

                <div className='relative z-10 flex h-full w-full flex-col justify-between p-7 sm:p-5 md:p-7'>
                    <div>
                        <span className='inline-flex items-center gap-1.5 rounded-pill bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white/90 backdrop-blur ring-1 ring-white/15'>
                            <FaShieldAlt size={9} /> ItemZo Marketplace
                        </span>
                        <h2 className='mt-4 text-3xl sm:text-2xl md:text-4xl font-black leading-[1.1] max-w-[20ch] tracking-tight'>
                            Rəqəmsal məhsullar üçün təhlükəsiz <span className='text-accent-300'>marketplace</span>
                        </h2>
                        <p className='mt-3 text-sm sm:text-[13px] text-white/75 max-w-md leading-relaxed'>
                            Oyun hesabları, gift kartlar, abunəliklər və digital xidmətləri etibarlı satıcılardan alın.
                        </p>
                    </div>
                    <div className='mt-6 flex flex-wrap items-center gap-3'>
                        <Link
                            to='/market'
                            className='inline-flex items-center gap-2 rounded-card bg-white px-5 py-2.5 text-sm font-bold text-brand-700 hover:bg-slate-100 transition-colors shadow-card-hover'
                        >
                            Bazara bax <FaArrowRight size={12} />
                        </Link>
                        <Link
                            to='/magazalar'
                            className='inline-flex items-center gap-2 rounded-card border border-white/30 bg-white/5 px-5 py-2.5 text-sm font-bold text-white hover:bg-white/10 transition-colors backdrop-blur'
                        >
                            Mağazalara bax
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='relative h-full w-full overflow-hidden rounded-card border border-slate-200 bg-slate-100 shadow-card group'>
            {/* fallback qradiyent — şəkil yüklənməsə də səliqəli görünür */}
            <div className='absolute inset-0 z-0 bg-gradient-to-br from-brand-700 to-indigo-800' aria-hidden='true' />
            {banners.map((b, i) => {
                const href = linkTarget(b.link)
                return (
                    <div
                        key={b._id || i}
                        className={`absolute inset-0 transition-opacity duration-700 motion-reduce:transition-none ${i === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                        aria-hidden={i !== idx}
                    >
                        <img
                            src={b.banner}
                            alt={b.productId?.name ? `${b.productId.name} - ItemZo` : 'ItemZo bazarı'}
                            className='h-full w-full object-cover transition-transform duration-700 ease-out motion-reduce:transition-none'
                            loading={i === 0 ? 'eager' : 'lazy'}
                            decoding='async'
                            onError={(e) => { e.currentTarget.style.display = 'none' }}
                        />
                        {href && <Link to={href} aria-label='Banneri aç' tabIndex={i === idx ? 0 : -1} className='absolute inset-0' />}
                    </div>
                )
            })}

            {banners.length > 1 && (
                <>
                    <button
                        type='button'
                        onClick={prev}
                        aria-label='Əvvəlki'
                        className='absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-card opacity-0 transition-opacity duration-200 group-hover:opacity-100 sm:hidden'
                    >
                        <FaChevronLeft size={12} />
                    </button>
                    <button
                        type='button'
                        onClick={next}
                        aria-label='Növbəti'
                        className='absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-card opacity-0 transition-opacity duration-200 group-hover:opacity-100 sm:hidden'
                    >
                        <FaChevronRight size={12} />
                    </button>

                    {/* Dots */}
                    <div className='absolute bottom-2 left-1/2 z-20 -translate-x-1/2 flex items-center rounded-pill bg-black/30 px-1 py-0.5 backdrop-blur sm:bottom-1.5'>
                        {banners.map((_, i) => (
                            <button
                                key={i}
                                type='button'
                                onClick={() => setIdx(i)}
                                aria-label={`Slayd ${i + 1}`}
                                className='flex h-8 w-8 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-white'
                            >
                                <span aria-hidden='true' className={`h-1.5 rounded-full transition-all duration-300 motion-reduce:transition-none ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/50 group-hover:bg-white/80'}`} />
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

// ── Side promo tile ──────────────────────────────────────────
const SideTile = ({ banners = [], fallbackTitle, fallbackSubtitle, fallbackTo, fallbackIcon, fallbackTone = 'brand' }) => {
    const { idx, setIdx } = useSlider(banners.length, 4500)

    if (!banners.length) {
        const tone = fallbackTone === 'accent'
            ? 'from-accent-500 via-accent-600 to-accent-700'
            : 'from-brand-500 via-brand-600 to-brand-700'

        return (
            <Link
                to={fallbackTo || '/market'}
                className={`relative flex h-full w-full items-center justify-between overflow-hidden rounded-card bg-gradient-to-br ${tone} p-4 text-white shadow-card transition-transform hover:-translate-y-0.5`}
            >
                <div className='min-w-0'>
                    <p className='text-xs font-semibold uppercase tracking-wider text-white/80'>{fallbackSubtitle}</p>
                    <p className='mt-1 text-base font-bold leading-snug truncate'>{fallbackTitle}</p>
                    <span className='mt-2 inline-flex items-center gap-1 text-xs font-semibold text-white/90'>
                        İndi bax <FaArrowRight size={10} />
                    </span>
                </div>
                <div className='shrink-0 ml-2 inline-flex h-10 w-10 items-center justify-center rounded-card bg-white/20 backdrop-blur'>
                    {fallbackIcon}
                </div>
            </Link>
        )
    }

    const b = banners[idx]
    const href = linkTarget(b?.link)
    const productName = b?.productId?.name

    const TileInner = (
        <>
            {/* fallback qradiyent — şəkil yüklənməsə də səliqəli görünür */}
            <div className='absolute inset-0 z-0 bg-gradient-to-br from-brand-600 to-indigo-700' aria-hidden='true' />
            {banners.map((item, i) => (
                <div
                    key={item._id || i}
                    className={`absolute inset-0 transition-opacity duration-500 ${i === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                    aria-hidden={i !== idx}
                >
                    <img
                        src={item.banner}
                        alt={item.productId?.name ? `${item.productId.name} - ItemZo` : 'ItemZo kampaniyası'}
                        className='h-full w-full object-cover'
                        loading='lazy'
                        decoding='async'
                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                    />
                </div>
            ))}

            {b?.category && (
                <div className='absolute left-2 top-2 z-30 rounded-pill bg-warning-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-900'>
                    {b.category}
                </div>
            )}

            <div className='absolute inset-x-0 bottom-0 z-30 bg-gradient-to-t from-black/55 via-black/25 to-transparent p-3'>
                {productName && (
                    <p className='truncate text-xs font-semibold text-white drop-shadow'>{productName}</p>
                )}
                <div className='mt-1 flex items-center justify-between gap-2'>
                    <span className='inline-flex items-center gap-1 text-[11px] font-semibold text-white/90'>
                        İndi bax <FaArrowRight size={9} />
                    </span>
                    {banners.length > 1 && (
                        <div className='flex items-center gap-1'>
                            {banners.map((_, i) => (
                                <button
                                    key={i}
                                    type='button'
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i) }}
                                    aria-label={`Slayd ${i + 1}`}
                                    className='flex h-7 w-7 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-white'
                                >
                                    <span aria-hidden='true' className={`h-1.5 rounded-full transition-all motion-reduce:transition-none ${i === idx ? 'w-3 bg-white' : 'w-1.5 bg-white/60'}`} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    )

    return href ? (
        <Link
            to={href}
            className='relative block h-full w-full overflow-hidden rounded-card border border-slate-200 bg-slate-100 shadow-card transition-transform hover:-translate-y-0.5'
        >
            {TileInner}
        </Link>
    ) : (
        <div className='relative h-full w-full overflow-hidden rounded-card border border-slate-200 bg-slate-100 shadow-card'>
            {TileInner}
        </div>
    )
}

// ── Skeleton (mobile-first, aspect-ratio driven) ─────────────
const BannerSkeleton = () => (
    <div data-testid='banner-skeleton' className='grid h-[clamp(400px,32vw,440px)] w-full min-w-0 grid-cols-[minmax(0,2fr)_minmax(280px,1fr)] gap-3 md-lg:h-auto md-lg:grid-cols-1 md-lg:gap-2.5 sm:gap-2'>
        <div className='h-full min-h-0 min-w-0 md-lg:aspect-[11/5] md-lg:max-h-[400px] sm:aspect-[2/1] sm:max-h-none'>
            <Skeleton radius='card' className='h-full w-full' />
        </div>
        <div className='grid h-full min-h-0 min-w-0 grid-rows-2 gap-3 md-lg:h-auto md-lg:grid-cols-2 md-lg:grid-rows-1 md-lg:gap-2.5 sm:gap-2 2xs:grid-cols-1'>
            <Skeleton radius='card' className='h-full min-h-0 w-full md-lg:aspect-[16/9]' />
            <Skeleton radius='card' className='h-full min-h-0 w-full md-lg:aspect-[16/9]' />
        </div>
    </div>
)

// ── Main component ───────────────────────────────────────────
const Banner = () => {
    const dispatch = useDispatch()
    const {
        mainBanners = [],
        topSellerBanners = [],
        bottomSellerBanners = [],
        banners = [],
        loader = true
    } = useSelector(state => state.banner)

    useEffect(() => {
        dispatch(get_banners())
    }, [dispatch])

    const leftBanners = mainBanners.length > 0 ? mainBanners : banners

    return (
        <section className='w-full max-w-marketplace mx-auto px-4 sm:px-4 md-lg:px-5 md:px-5 lg:px-8 pt-4'>
            {loader ? (
                <BannerSkeleton />
            ) : (
                <div data-testid='banner-layout' className='grid h-[clamp(400px,32vw,440px)] w-full min-w-0 grid-cols-[minmax(0,2fr)_minmax(280px,1fr)] gap-3 md-lg:h-auto md-lg:grid-cols-1 md-lg:gap-2.5 sm:gap-2'>
                    {/* Main hero — shared desktop height, compact responsive ratio below 991px */}
                    <div data-testid='banner-main-slot' className='h-full min-h-0 min-w-0 md-lg:aspect-[11/5] md-lg:max-h-[400px] sm:aspect-[2/1] sm:max-h-none'>
                        <div className='h-full min-h-0 min-w-0 overflow-hidden'>
                            <MainHero banners={leftBanners} />
                        </div>
                    </div>

                    {/* Side tiles — equal desktop rows, 2-col tablet/mobile, stacked at <=340px */}
                    <div data-testid='banner-secondary-column' className='grid h-full min-h-0 min-w-0 grid-rows-2 gap-3 md-lg:h-auto md-lg:grid-cols-2 md-lg:grid-rows-1 md-lg:gap-2.5 sm:gap-2 2xs:grid-cols-1'>
                        <div data-testid='banner-top-slot' className='h-full min-h-0 min-w-0 overflow-hidden md-lg:aspect-[16/9]'>
                            <SideTile
                                banners={topSellerBanners}
                                fallbackTitle='Həftənin fürsətləri'
                                fallbackSubtitle='Kampaniya'
                                fallbackTo='/bazar?type=weekly'
                                fallbackTone='accent'
                                fallbackIcon={<FaBolt />}
                            />
                        </div>
                        <div data-testid='banner-bottom-slot' className='h-full min-h-0 min-w-0 overflow-hidden md-lg:aspect-[16/9]'>
                            <SideTile
                                banners={bottomSellerBanners}
                                fallbackTitle='Önə çıxarılan elanlar'
                                fallbackSubtitle='Seçilmiş'
                                fallbackTo='/bazar?type=bumped'
                                fallbackTone='brand'
                                fallbackIcon={<FaShieldAlt />}
                            />
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}

export default Banner
